import {
  InputHTMLAttributes,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  FieldValues,
  UseFormRegister,
  Path,
  FieldErrors,
  RegisterOptions,
} from 'react-hook-form'
import { isEmpty, noop } from 'lodash'
import { SvgUpload, SvgUploadingIcon } from '../../assets/images'
import { AxiosProgressEvent } from 'axios'
import FileUploadProgress from './UploadProgressBar'
import {
  FILE_TYPE_TOASTER,
  MODE_CONSTANTS,
  MULTI_UPLOAD_FILE_TYPE,
  NAMESPACE,
  openNewWindow,
  FILE_EXTENSIONS,
  ATTACHMENT_FIELD_OPTIONS,
  isValidFileName,
} from '../../utils'
import Modal from '../Modal'
import Tooltip from '../Tooltip'
import {
  FileDataFromExtraResource,
  MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE,
  ToasterTypeValues,
} from '../../utils/types/fileUploadTypes'
import CustomButton from '../CustomButton'
import Loader from '../Loader'
import moment from 'moment'
import { SvgNewTab } from '../../icons'

interface MultiSelectFileInputPropTypes<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  name: Path<T>
  label?: string
  register?: UseFormRegister<T>
  errors?: FieldErrors<T>
  className?: string
  validation?: RegisterOptions<T, Path<T>>
  commentLabel?: string
  accept?: string
  comment?: string
  isRequired?: boolean
  labelClass?: string
  commentLabelClass?: string
  dataQaId?: string
  toolTip?: string
  maxSizeOfFile?: string | undefined
  minSizeOfFile?: string | undefined
  handleToaster?: (message: string, type: ToasterTypeValues) => void
  mode?: (typeof MODE_CONSTANTS)[keyof typeof MODE_CONSTANTS]
  onChangeValue: (
    filesList: File[] | null,
    index?: number,
    onProgress?: (
      progressEvent: AxiosProgressEvent,
      index?: number | undefined
    ) => void
  ) => Promise<void>
  newData: {
    id: number
    _state: string
  }[]
  defaultData: FileDataFromExtraResource[]
  handleClearFileForEditMode: (
    index: number,
    clearForEdit?: boolean
  ) => Promise<void>

  handleBulkDownload: () => Promise<void>
  handleSingleDownload: (
    index: number,
    type?: string
  ) => Promise<urlData | null>
  isOpenForListing?: boolean
  setRenderMultiUpload?: (
    value: React.SetStateAction<{
      isOpen: boolean
      element: undefined
      id: string | undefined
    }>
  ) => void
  noDataFound?: boolean
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  isUploadDisabled?: boolean
  selectedFile?: { index: number; url: string }
  setSelectedFile?: (value?: number) => Promise<void>
  showSplitFax?: boolean
  renderSplitFax?: () => ReactElement
  aePcDocId?: number | null
  docEntityId?: number
  fromQueueTable?: boolean
  customHeader?: string
  onEditClick?: () => void
  onPopoutClick?: () => void
  isReadOnlyUser?: boolean
  isEditButtonVisible?: boolean
}

export interface urlData {
  url: string
}

const MultiSelectFileInput = <T extends FieldValues>(
  props: MultiSelectFileInputPropTypes<T>
) => {
  const {
    name,
    label,
    disabled = false,
    register,
    errors,
    validation,
    className = '',
    commentLabel = '',
    comment = '',
    accept = '',
    readOnly = false,
    isRequired,
    labelClass = 'text-xs',
    commentLabelClass = 'text-xs',
    dataQaId,
    toolTip,
    maxSizeOfFile,
    minSizeOfFile,
    handleToaster,
    mode,
    onChangeValue,
    newData,
    defaultData,
    handleClearFileForEditMode,
    handleBulkDownload,
    handleSingleDownload,
    isOpenForListing,
    setRenderMultiUpload,
    noDataFound,
    files,
    setFiles,
    isUploadDisabled,
    selectedFile,
    setSelectedFile,
    showSplitFax,
    renderSplitFax,
    aePcDocId,
    docEntityId,
    fromQueueTable,
    customHeader,
    onEditClick,
    onPopoutClick,
    isReadOnlyUser,
    isEditButtonVisible,
    ...rest
  } = props

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const {
    ref: registerRef,
    onChange: fromOnchange,
    ...registerRest
  } = register?.(name, {
    ...validation,
    validate: (value) => {
      if (!isRequired) return true
      if (fileNames.length > 0) return true
      return value && value.length > 0 ? true : NAMESPACE.REQUIRED
    },
  }) || {}

  const [filePreviews, setFilePreviews] = useState<string[]>([])
  const [isFile, setIsFile] = useState<boolean>(false)
  const [fileNames, setFileNames] = useState<string[]>([])
  const [isPreview, setIsPreview] = useState<boolean>(false)
  const [previewForViewAndEdit, setPreviewForViewAndEdit] = useState<
    string | null
  >(null)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(
    isOpenForListing || false
  )
  const [uploadProgress, setUploadProgress] = useState<AxiosProgressEvent[]>([])
  const [loaderForBulkDownloadFile, setLoaderForBulkDownloadFile] =
    useState(false)
  const viewMode = mode === NAMESPACE.VIEW
  const editMode = mode === NAMESPACE.EDIT
  const addMode = mode === NAMESPACE.NEW_ADD
  useEffect(() => {
    if (files.length > 0) {
      setIsFile(true)
      const previews = files.map((file) => URL.createObjectURL(file))
      setFilePreviews(previews)
      const names = files.map((file) => file.name)
      setFileNames(names)
    } else {
      setIsFile(false)
      setFilePreviews([])
      setFileNames([])
    }
    return () => {
      filePreviews.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [files])

  useEffect(() => {
    // Newly Upload files list cleared after it's saved and defaultData is refetched
    setFiles([])
  }, [defaultData])

  const onProgress = (
    progressEvent: AxiosProgressEvent,
    index?: number | undefined
  ) => {
    if (index !== undefined && index !== null) {
      if (newData?.length) {
        setUploadProgress((prevProgress) => {
          const updatedProgress = [...prevProgress]
          updatedProgress[newData?.length + index] = progressEvent
          return updatedProgress
        })
      } else {
        setUploadProgress((prevProgress) => {
          const updatedProgress = [...prevProgress]
          updatedProgress[index] = progressEvent
          return updatedProgress
        })
      }
    }
  }
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFileList = Array.from(event.target.files ?? [])

    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }, 0)
    const acceptedTypeListArray = accept?.split(', ')?.map((val) => {
      return MULTI_UPLOAD_FILE_TYPE[
        val.slice(1).toUpperCase() as keyof typeof MULTI_UPLOAD_FILE_TYPE
      ]
    })
    const acceptedExtensions = accept
      ?.split(', ')
      ?.map((val) => val.toLowerCase())
    const uniqueList = []
    const commonList = []
    const nameNotAllowedList = []
    const acceptedTypeList = []
    const fileSizeGreaterList = []
    const fileSizeSmallerList = []

    for (let i = 0; i < selectedFileList?.length; i++) {
      const file = selectedFileList[i]
      const fileName = file.name

      const baseName = fileName.includes('.')
        ? fileName.split('.').slice(0, -1).join('.')
        : fileName
      const isCommon = files.some(
        (file) => file.name === selectedFileList[i].name
      )
      const defaultFilePresent = defaultData?.some(
        (file) =>
          file?.name === selectedFileList[i].name &&
          file?.content_type === selectedFileList[i].type
      )
      const fileExtension =
        '.' + selectedFileList[i].name.split('.').pop()?.toLowerCase()
      const isTypeAccepted = acceptedTypeListArray.includes(
        selectedFileList[i].type as MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE
      )
      const isExtensionAccepted = acceptedExtensions?.includes(fileExtension)
      if (isCommon || defaultFilePresent) {
        commonList.push(selectedFileList[i])
      } else if (!isTypeAccepted && !isExtensionAccepted) {
        acceptedTypeList.push(selectedFileList[i])
      } else if (
        selectedFileList[i]?.size / 1024 >
        (maxSizeOfFile ? +maxSizeOfFile : 5120)
      ) {
        fileSizeGreaterList.push(selectedFileList[i])
      } else if (
        selectedFileList[i]?.size / 1024 <
        (minSizeOfFile ? +minSizeOfFile : 100)
      ) {
        fileSizeSmallerList.push(selectedFileList[i])
      } else if (!isValidFileName(baseName)) {
        nameNotAllowedList.push(selectedFileList[i])
      } else {
        uniqueList.push(selectedFileList[i])
      }
    }
    if (acceptedTypeList?.length) {
      // const nameString = acceptedTypeList
      //   ?.map((val) => val?.name)
      //   .filter((name) => name)
      //   .join(', ')
      // const message = `[${nameString}] ${
      //   acceptedTypeList?.length > 1 ? 'files' : 'file'
      // } type is not allowed.`                                    //NOTE: Changed as per bug 23139
      const message = NAMESPACE.INVALID_FILE_TYPE_MSG
      handleToaster && handleToaster(message, 'error')
    }
    if (fileSizeGreaterList?.length) {
      const nameString = fileSizeGreaterList
        ?.map((val) => val?.name)
        .filter((name) => name)
        .join(', ')
      const message = `${
        fileSizeGreaterList?.length > 1 ? 'Files' : 'File'
      } [${nameString}] size exceed the maximum limit of ${
        maxSizeOfFile || 5120
      } KB.`
      handleToaster && handleToaster(message, 'error')
    }
    if (nameNotAllowedList?.length) {
      const nameString = nameNotAllowedList
        ?.map((val) => val?.name)
        .filter((name) => name)
        .join(', ')
      const message = `${
        nameNotAllowedList?.length > 1 ? 'Files' : 'File'
      } [${nameString}] contain invalid characters.`
      handleToaster && handleToaster(message, 'error')
    }
    if (fileSizeSmallerList?.length) {
      const nameString = fileSizeSmallerList
        ?.map((val) => val?.name)
        .filter((name) => name)
        .join(', ')
      const message = `${
        fileSizeSmallerList?.length > 1 ? 'Files' : 'File'
      } [${nameString}] is below the minimum allowed limit of ${
        minSizeOfFile || 100
      } KB.`
      handleToaster && handleToaster(message, 'error')
    }
    if (commonList?.length) {
      const nameString = commonList
        ?.map((val) => val?.name)
        .filter((name) => name)
        .join(', ')
      const message = `${
        commonList?.length > 1 ? 'Files' : 'File'
      } [${nameString}] have already been uploaded.`
      handleToaster && handleToaster(message, 'error')
    }
    if (uniqueList?.length) {
      setUploadProgress((prev) => [
        ...prev,
        ...Array(uniqueList.length).fill(0),
      ])
      const newFiles = uniqueList
      setFiles((prevFiles) => [...prevFiles, ...newFiles])
      setFileNames((prevFileNames) => [
        ...prevFileNames,
        ...newFiles.map((file) => file.name),
      ])
      onChangeValue(uniqueList, undefined, onProgress)

      fromOnchange?.({
        target: {
          name,
          value: [...files, ...newFiles],
        },
      })
      setIsDropdownOpen(true)
    }
  }
  const auditHistoryValueData = (file: FileDataFromExtraResource) => {
    let auditHistoryValue = ''

    if (file?.created_at) {
      auditHistoryValue += `| ${moment(file.created_at).format(
        'MM/DD/YYYY hh:mm A'
      )}`
    }

    if (file?.created_by) {
      auditHistoryValue += ` | ${file.created_by}`
    }
    return auditHistoryValue
  }
  const handleClearFile = async (index: number, clearForEdit?: boolean) => {
    try {
      await handleClearFileForEditMode(index, clearForEdit)
      let updatedFiles
      if (clearForEdit) {
        updatedFiles = files.filter((_, i) => i !== index)
        setFilePreviews((prev) => prev.filter((_, i) => i !== index))
        setUploadProgress((prev) => prev.filter((_, i) => i !== index))
        setFileNames(updatedFiles.map((file) => file.name))
        if (updatedFiles.length === 0) {
          setIsFile(false)
        }
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      await onChangeValue(null, index)
      fromOnchange?.({
        target: {
          name,
          value: updatedFiles,
        },
      })
      setPreviewIndex(-1)
      setSelectedFile && setSelectedFile(undefined)
    } catch (error) {
      console.error(error)
    }
  }
  const handlePreviewToggle = async (
    index: number,
    fileType: MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE,
    type?: string,
    editModeForDownload?: boolean,
    isDownload?: boolean,
    fileName?: string
  ) => {
    const fileExtension = fileName?.toLowerCase().split('.').pop() || ''

    const isOfficeDocument =
      fileType === MULTI_UPLOAD_FILE_TYPE.DOC ||
      fileType === MULTI_UPLOAD_FILE_TYPE.DOCX ||
      fileType === MULTI_UPLOAD_FILE_TYPE.DOCM ||
      fileType === MULTI_UPLOAD_FILE_TYPE.XLSX ||
      fileType === MULTI_UPLOAD_FILE_TYPE.XLSM ||
      fileType === MULTI_UPLOAD_FILE_TYPE.XLS ||
      fileType === MULTI_UPLOAD_FILE_TYPE.PPT ||
      fileType === MULTI_UPLOAD_FILE_TYPE.PPTX ||
      fileExtension === FILE_EXTENSIONS.DOC ||
      fileExtension === FILE_EXTENSIONS.DOCX ||
      fileExtension === FILE_EXTENSIONS.DOCM ||
      fileExtension === FILE_EXTENSIONS.XLSX ||
      fileExtension === FILE_EXTENSIONS.XLSM ||
      fileExtension === FILE_EXTENSIONS.XLS ||
      fileExtension === FILE_EXTENSIONS.PPT ||
      fileExtension === FILE_EXTENSIONS.PPTX

    const shouldDownloadDirectly =
      fileType === MULTI_UPLOAD_FILE_TYPE.CSV ||
      fileType === MULTI_UPLOAD_FILE_TYPE.EML ||
      fileType === MULTI_UPLOAD_FILE_TYPE.DOCM ||
      fileType === MULTI_UPLOAD_FILE_TYPE.XLSM ||
      fileExtension === FILE_EXTENSIONS.CSV ||
      fileExtension === FILE_EXTENSIONS.EML ||
      fileExtension === FILE_EXTENSIONS.DOCM ||
      fileExtension === FILE_EXTENSIONS.XLSM

    if (viewMode || editModeForDownload) {
      const data = await handleSingleDownload(index, type)
      if (data?.url) {
        setPreviewForViewAndEdit(data?.url)
        setPreviewIndex(null)
        if (isDownload || (!isDownload && shouldDownloadDirectly)) {
          try {
            const response = await fetch(data.url)
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download =
              fileName || ATTACHMENT_FIELD_OPTIONS.DOWNLOAD_LOWERCASE
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(blobUrl)
          } catch (error) {
            console.error('Error downloading file:', error)
          }
          return
        }

        if (isOfficeDocument) {
          openNewWindow(
            `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              data?.url
            )}`
          )
          return
        }
        openNewWindow(data?.url)
      }
      return
    }

    if (
      fileType?.includes('application') &&
      fileType !== MULTI_UPLOAD_FILE_TYPE.PDF &&
      !isOfficeDocument &&
      type
    ) {
      handleToaster &&
        handleToaster(FILE_TYPE_TOASTER.FILE_TYPE_NOT_SUPPORTED, 'error')
      return
    }

    setPreviewIndex(index)
    setIsPreview((prev) => !prev)
  }

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const toggleDropdown = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsDropdownOpen((prev) => !prev)
    setSelectedFile && setSelectedFile(0)
  }

  return (
    <div className={`relative w-full group ${className}`}>
      {!isOpenForListing ? (
        <Tooltip content={toolTip || ''}>
          {label ? (
            <div
              className={`translate-x-[0.875rem] translate-y-[-0.75rem] flex text-base p-0 origin-top-left truncate max-w-[calc(100%-1.75rem)] absolute left-0 top-[0.1rem] bg-white z-[1] pointer-events-none${
                disabled ? 'text-accent-300' : 'text-black'
              }`}
            >
              <label
                className={`${
                  viewMode ? 'font-medium break-words ' : 'font-extraLight'
                } block leading-1 font-primary truncate max-w-[calc(100% - 1.5rem)] mb-0 -z-20 ${
                  disabled
                    ? 'text-accent-300'
                    : viewMode
                    ? ''
                    : 'group-hover:text-blue-600'
                } ${labelClass}`}
                title={label}
              >
                {label}
              </label>
              {commentLabel ? (
                <span
                  className={`ml-1 leading-1 truncate max-w-[calc(100% - 1.5rem)] text-accent-300 ${commentLabelClass}`}
                >
                  {commentLabel}
                </span>
              ) : null}
              {isRequired ? (
                <span
                  className={`ml-1 text-xs leading-1 ${
                    disabled ? 'text-accent-300' : 'text-red-500'
                  }`}
                >
                  *
                </span>
              ) : null}
            </div>
          ) : null}
          <div
            className={`relative p-1 h-10 ${
              viewMode ? `` : 'border border-border-200'
            } rounded cursor-default ${
              disabled
                ? 'border-border-200 bg-accent-50'
                : 'group-hover:border-secondary-500'
            } ${
              !isEmpty(errors?.[name])
                ? 'border-error-500'
                : 'border-border-150'
            }`}
          >
            <input
              type="file"
              accept={accept}
              multiple
              {...rest}
              {...registerRest}
              ref={(e) => {
                registerRef?.(e)
                fileInputRef.current = e
              }}
              onChange={handleFileChange}
              className="hidden"
              data-qa-id={dataQaId || ''}
            />
            <div className="flex items-center justify-between w-full">
              {files?.length ||
              (viewMode && defaultData?.length) ||
              (editMode && defaultData?.length) ||
              (addMode && defaultData?.length) ? (
                <div
                  onClick={toggleDropdown}
                  className="flex flex-row align-middle p-1 font-primary font-extralight text-xs pl-2 cursor-pointer text-blue-600 group-hover:underline"
                >
                  <span className="!truncate">{NAMESPACE.VIEW_DOCUMENTS}</span>
                </div>
              ) : null}
              {!viewMode && !disabled ? (
                <div
                  className={`flex cursor-pointer ml-auto ${
                    files?.length || defaultData?.length ? '' : 'pt-2'
                  }`}
                  onClick={readOnly || disabled ? noop : handleUploadFile}
                  tabIndex={rest?.tabIndex}
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      e?.stopPropagation()
                      e?.preventDefault()
                      if (!readOnly && !disabled) handleUploadFile()
                    }
                  }}
                >
                  <span>
                    {' '}
                    <SvgUpload />
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </Tooltip>
      ) : null}
      {isDropdownOpen &&
        ((isFile && files.length > 0) ||
          viewMode ||
          editMode ||
          (addMode && defaultData?.length)) && (
          <ul className="mt-2">
            <Modal
              isOpen={isDropdownOpen}
              closeModal={() => {
                setIsDropdownOpen(false)
                setRenderMultiUpload &&
                  setRenderMultiUpload((prev) => ({ ...prev, isOpen: false }))
              }}
              showCloseButton={true}
              allowOutsideClick
            >
              {isOpenForListing && (
                <div className="hidden">
                  {' '}
                  <input
                    type="file"
                    accept={accept}
                    multiple
                    {...rest}
                    {...registerRest}
                    ref={(e) => {
                      registerRef?.(e)
                      fileInputRef.current = e
                    }}
                    onChange={handleFileChange}
                    className="hidden"
                    data-qa-id={dataQaId || ''}
                  />
                </div>
              )}
              <div className="p-1 w-[64rem] overflow-auto">
                <div className="flex items-center justify-between border-b mb-4">
                  {!viewMode ? (
                    <span className="pl-2 font-medium">
                      {fromQueueTable ? customHeader : NAMESPACE.UPLOAD_FILES}
                    </span>
                  ) : (
                    <span className="pl-2 font-medium">
                      {' '}
                      {NAMESPACE.VIEW_DOCUMENTS}
                    </span>
                  )}
                  {fromQueueTable && onEditClick && onPopoutClick ? (
                    <div className="flex pr-6 items-center pb-2">
                      {isEditButtonVisible ? (
                        <>
                          <CustomButton
                            dataQaId={'review-button'}
                            type="button"
                            color="primary"
                            onClick={onEditClick}
                            className="h-10 "
                            isFilled={true}
                            disabled={isReadOnlyUser}
                          >
                            <div className="flex items-center justify-center w-full gap-1 svg-path-stroke-white path">
                              <span>{NAMESPACE.EDIT_BUTTON}</span>
                            </div>
                          </CustomButton>
                          <div className="h-6 border-l border-border-300 mx-2" />
                        </>
                      ) : null}
                      <button
                        className={`cursor-pointer inline-flex items-center`}
                        onClick={onPopoutClick}
                        type="button"
                      >
                        <SvgNewTab width={16} height={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-row-reverse pr-2">
                      {(viewMode || editMode) && defaultData?.length ? (
                        <>
                          <CustomButton
                            type="button"
                            height=".625rem"
                            className="shadow p-1 m-1 bg-primary-500 text-white "
                            widthClass="w-[6.938rem]"
                            onClick={async (e) => {
                              e?.stopPropagation()
                              setLoaderForBulkDownloadFile(true)
                              await handleBulkDownload()
                              setLoaderForBulkDownloadFile(false)
                            }}
                            disabled={!!loaderForBulkDownloadFile}
                          >
                            {!loaderForBulkDownloadFile ? (
                              <span> {NAMESPACE.DOWNLOAD_ALL}</span>
                            ) : (
                              <span className="animate-spin p-1 text-white">
                                {<SvgUploadingIcon className="h-full w-full" />}
                              </span>
                            )}
                          </CustomButton>
                          {aePcDocId && (
                            <CustomButton
                              height=".625rem"
                              className="p-1 m-1"
                              isFilled
                              onClick={() => {
                                openNewWindow(
                                  `/manage-queue/dynamic-queue/${docEntityId}/${aePcDocId}`
                                )
                              }}
                            >
                              {NAMESPACE?.AE_PC_REVIEW}
                            </CustomButton>
                          )}
                        </>
                      ) : null}
                      {(!viewMode && !disabled) || isOpenForListing ? (
                        <CustomButton
                          type="button"
                          height=".625rem"
                          className="shadow p-1 m-1 "
                          widthClass="w-[4.5rem]"
                          onClick={
                            (readOnly || disabled) && !isOpenForListing
                              ? noop
                              : handleUploadFile
                          }
                          disabled={isUploadDisabled}
                        >
                          {NAMESPACE.UPLOAD}
                        </CustomButton>
                      ) : null}
                      <CustomButton
                        type="button"
                        height=".625rem"
                        className="shadow p-1 m-1 "
                        widthClass="w-[3.813rem]"
                        onClick={() => {
                          setIsDropdownOpen(false)
                          setRenderMultiUpload &&
                            setRenderMultiUpload((prev) => ({
                              ...prev,
                              isOpen: false,
                            }))
                        }}
                      >
                        {NAMESPACE.CLOSE}
                      </CustomButton>
                    </div>
                  )}
                </div>

                <div
                  className={`tp-2 max-h-[25rem] min-h-[6rem] overflow-auto ${
                    isOpenForListing && noDataFound
                      ? 'content-center text-center'
                      : ''
                  }`}
                >
                  {isOpenForListing &&
                    !defaultData?.length &&
                    !files?.length &&
                    (!noDataFound ? (
                      <Loader
                        loaderHeight="4rem"
                        loaderWidth="4rem"
                        isGlobal={false}
                      />
                    ) : (
                      !files?.length &&
                      noDataFound &&
                      !defaultData?.length && (
                        <span className="content-center text-center">
                          {NAMESPACE.NO_FILES_FOUND}
                        </span>
                      )
                    ))}

                  <div className="flex bg-gray-50 gap-2 p-2">
                    {defaultData?.length
                      ? defaultData.map(
                          (file: FileDataFromExtraResource, index) => (
                            <li
                              key={`${file?.name ?? ''}-${index}`}
                              className={`flex items-center rounded border-gray-50 ${
                                selectedFile?.index === index &&
                                '!border-[#3396F3]'
                              } border-2 cursor-pointer`}
                              onClick={() => {
                                setSelectedFile && setSelectedFile(index)
                              }}
                            >
                              {
                                <FileUploadProgress
                                  key={`${file?.name ?? ''}-${index}`}
                                  fileName={file?.name}
                                  totalSize={file.size}
                                  onCancel={handleClearFile}
                                  index={index}
                                  handlePreviewToggle={handlePreviewToggle}
                                  fileType={
                                    (file.content_type ??
                                      file.contentType) as MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE
                                  }
                                  viewMode={
                                    !!(
                                      viewMode ||
                                      (addMode && defaultData?.length)
                                    )
                                  }
                                  editMode={isOpenForListing ? true : editMode}
                                  auditHistoryValue={auditHistoryValueData(
                                    file
                                  )}
                                  disabled={isOpenForListing ? false : disabled}
                                  tabIndex={rest?.tabIndex}
                                  isUploadDisabled={isUploadDisabled}
                                />
                              }
                            </li>
                          )
                        )
                      : null}
                  </div>
                  {addMode || editMode || isOpenForListing
                    ? files.map((file: File, index) => (
                        <li
                          key={`${file?.name ?? ''}-${index}`}
                          className="flex items-center rounded"
                        >
                          {
                            <FileUploadProgress
                              key={`${file?.name ?? ''}-${index}`}
                              uploadedSize={uploadProgress[index]}
                              fileName={file.name}
                              totalSize={file.size}
                              onCancel={handleClearFile}
                              index={index}
                              handlePreviewToggle={handlePreviewToggle}
                              fileType={
                                file.type as MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE
                              }
                              viewMode={isOpenForListing ? false : viewMode}
                              disableRemove={uploadProgress.some(
                                (val) => val?.progress !== 1
                              )}
                              disabled={isOpenForListing ? false : disabled}
                              isOpenForListing={isOpenForListing}
                              tabIndex={rest?.tabIndex}
                              isUploadDisabled={isUploadDisabled}
                            />
                          }
                        </li>
                      ))
                    : null}
                </div>
              </div>
              {showSplitFax && renderSplitFax ? renderSplitFax() : null}
            </Modal>
          </ul>
        )}
      {isPreview &&
        (previewIndex !== null || previewForViewAndEdit !== null) && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="relative bg-white p-4 rounded-lg">
              <Modal
                isOpen={isPreview}
                closeModal={() => setIsPreview(false)}
                showCloseButton={true}
              >
                <embed
                  src={
                    previewIndex
                      ? filePreviews[previewIndex]
                      : previewForViewAndEdit || ''
                  }
                  width="900rem"
                  height="900rem"
                  className="max-w-full max-h-[80vh] object-contain"
                />
              </Modal>
            </div>
          </div>
        )}
      {!viewMode && comment ? (
        <span className="text-black text-xs">{comment}</span>
      ) : null}
      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === 'required' && (
          <span className="text-error-500 text-xxs font-primary font-extraLight">
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
    </div>
  )
}

export default MultiSelectFileInput
